/**
 * Tenant-specific auth wrapper that initializes AuthContext with
 * tenant-scoped Keycloak client configuration and permission-based
 * access gating.
 *
 * Users without the `tenant.access` permission see an Access Denied
 * page. No hardcoded role names — the backend maps Keycloak roles to
 * permissions via RolePermissionDefaults (HLD-010).
 *
 * @module apps/tenant/AuthWrapper
 * @see TASK-TA-AUTH-WRAPPER
 *
 * NOTE: nginx server_name must match manage.stage.* for this SPA to be served
 * on the manage.stage.verisa.ai subdomain (see web/nginx.conf).
 */
import React from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { AuthProvider, useAuth } from "../../store/AuthContext";
import { EmptyState } from "../../components/shared/EmptyState";
import {
  ImpersonationBanner,
  IMPERSONATION_BANNER_MIN_HEIGHT,
} from "../../components/shared/ImpersonationBanner";
import { colors } from "../../theme/colors";
import { impersonationIdentityKey, toBannerBinding } from "../../api/impersonation";
import { PermissionProvider } from "../../store/PermissionContext";
import { usePermissionContext } from "../../store/PermissionContext";
import { LoginPage } from "../../components/shared/LoginPage";
import { AppFooter, type AppFooterState } from "../../components/shared/AppFooter";
import { useBranding } from "../../branding/BrandingProvider";
import { useLocale } from "../../i18n/I18nProvider";
import { LOCALE_TIME, formatLocaleDate } from "../../utils/format";

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const KEYCLOAK_URL: string = import.meta.env.VITE_KEYCLOAK_URL || "";

const KEYCLOAK_REALM: string = import.meta.env.VITE_KEYCLOAK_REALM || "verisa";

const TENANT_CLIENT_ID: string =
  import.meta.env.VITE_KEYCLOAK_CLIENT_ID || "verisa-tenant";

const keycloakRealmUrl: string | undefined = KEYCLOAK_URL
  ? `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}`
  : undefined;

// ---------------------------------------------------------------------------
// RequireTenantAccess -- permission-based gate (no hardcoded roles)
// ---------------------------------------------------------------------------

interface RequireTenantAccessProps {
  children: React.ReactNode;
}

function RequireTenantAccess({ children }: RequireTenantAccessProps): React.ReactElement {
  const { authenticated, initialized, logout } = useAuth();
  const {
    hasPermission,
    loading: permissionsLoading,
    impersonatedFailure,
    refetchPermissions,
  } = usePermissionContext();
  // The login page is PRE-AUTH, and so is this read: BrandingProvider fetched
  // the record with a public, unauthenticated request, so the legal footer
  // renders before any principal exists (SPEC-PLATFORM-BRANDING RULE-006a).
  const { branding, state } = useBranding();
  const { locale } = useLocale();

  // D-BR-17 maps the provider's THREE states onto AppFooter's three, here at the
  // mount, deliberately: `loading` becomes `hidden`, because before the first
  // response nothing is known and neither the legal notice nor the statement
  // that one is missing may be claimed; `unavailable` stays `unavailable`, which
  // is the fail-VISIBLE line of RULE-001 / D-BR-15. A fourth provider state
  // would fail to type-check on this line rather than resolve silently.
  const footerState: AppFooterState = state === "loading" ? "hidden" : state;

  if (!initialized || permissionsLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!authenticated) {
    return (
      <LoginPage
        // The app SUFFIX, not a product title: LoginPage reads the product name
        // from the BrandingProvider and composes "<product name> - Management"
        // in one place (D-BR-08, RULE-012). The footer slot below stays wired -
        // this is one of the two login surfaces RULE-006a mounts it on.
        appSuffix={t`Management`}
        footer={
          <AppFooter
            values={branding.legal_footer}
            state={footerState}
            locale={locale}
          />
        }
      />
    );
  }

  // INSIDE A BINDING a failed permissions fetch is a LOUD error with retry and
  // NEVER falls through to the Access Denied screen below, which carries the
  // one control that must not be reachable inside a binding: Logout
  // (SPEC-FRONTEND-RULE-095(8)).
  if (impersonatedFailure) {
    return (
      <EmptyState
        variant="error"
        title={t`The viewed user's permissions could not be loaded`}
        description={t`Nothing is shown until they load, so that no screen appears with the wrong permissions.`}
        actionText={t`Retry`}
        onAction={refetchPermissions}
      />
    );
  }

  if (!hasPermission("workspace.manage") && !hasPermission("tenant.access")) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h5" color="error" gutterBottom>
          <Trans>Access Denied</Trans>
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          <Trans>You do not have permission to access the tenant administration panel. Please contact your workspace administrator.</Trans>
        </Typography>
        <Button variant="outlined" onClick={() => logout()}>
          <Trans>Logout</Trans>
        </Button>
      </Box>
    );
  }

  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// TenantAuthWrapper -- configures AuthProvider for tenant scope
// ---------------------------------------------------------------------------

export interface TenantAuthWrapperProps {
  children: React.ReactNode;
}

/**
 * Wraps tenant routes with AuthProvider → PermissionProvider → access gate.
 *
 * PermissionProvider is placed inside the wrapper (above the gate) so the
 * permission check has access to the fetched permission set. In dev mode
 * (`VITE_AUTH_DISABLED=true`) the wildcard `"*"` permission grants access
 * automatically.
 */
export function TenantAuthWrapper({
  children,
}: TenantAuthWrapperProps): React.ReactElement {
  return (
    <AuthProvider keycloakUrl={keycloakRealmUrl} clientId={TENANT_CLIENT_ID}>
      {/* The impersonation banner: OUTSIDE every feature gate and outside
          RequireTenantAccess, so no release toggle, tenant surface state or
          permission can suppress it, and Access Denied can never render
          without it (SPEC-FRONTEND-RULE-095(1), EPIC-IMPERSONATION-AC-004). */}
      <ImpersonationBannerSlot />
      <TenantIdentityBoundary>
        <PermissionProvider>
          <RequireTenantAccess>{children}</RequireTenantAccess>
        </PermissionProvider>
      </TenantIdentityBoundary>
    </AuthProvider>
  );
}

/**
 * MOUNT POINT 1 OF 3 of the identity-keyed remount (SPEC-FRONTEND-RULE-095(2),
 * blocking finding B7). The key wraps a node that CONTAINS PermissionProvider:
 * the token deliberately never changes when the act-as header arms, so the
 * permission hook's effect would never re-run and clearing the query client
 * does nothing for it. Only a remount resolves the target's permissions.
 */
export function TenantIdentityBoundary({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const identityKey = impersonationIdentityKey(useAuth().impersonation ?? null);
  return (
    <React.Fragment key={identityKey}>{children}</React.Fragment>
  );
}

// ---------------------------------------------------------------------------
// ImpersonationBannerSlot -- shared by the tenant and workspace mounts
// ---------------------------------------------------------------------------

/** The fixed strip that carries the failed-exit notice, placed like the banner. */
const exitFailedStripSx: SxProps<Theme> = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  minHeight: IMPERSONATION_BANNER_MIN_HEIGHT,
  zIndex: (theme: Theme) => theme.zIndex.appBar + 1,
  backgroundColor: colors.background.secondary,
  borderBottom: "1px solid",
  borderColor: colors.status.error,
};

/**
 * The impersonation banner slot (SPEC-FRONTEND-RULE-095(1), UI-049). The tenant
 * and workspace mounts place it ABOVE every feature gate and outside every
 * permission gate; it renders nothing while no binding is live. It fetches
 * nothing: the descriptor and the exit come from the session shell
 * (AuthContext), per TASK-IMP-BANNER-COMPONENT's contract. When the exit call
 * FAILED it renders the persistent notice naming when the view ends on its own
 * instead - never a pretended success (RULE-095(9), RULE-045).
 */
export function ImpersonationBannerSlot(): React.ReactElement | null {
  const {
    impersonation,
    impersonationStatus,
    impersonationTerminalCode,
    impersonationExitFailure,
    exitImpersonation,
    returnFromImpersonation,
  } = useAuth();

  if (impersonationExitFailure) {
    // A named local, never a member expression, so the msgid carries {time}.
    const time = formatLocaleDate(impersonationExitFailure.expiresAt, LOCALE_TIME);
    return (
      <Box sx={exitFailedStripSx} data-testid="impersonation-exit-failed">
        <EmptyState
          compact
          variant="error"
          title={t`The view could not be ended.`}
          description={t`It ends on its own at ${time}. Every action until then is still logged under your name.`}
          actionText={t`Back to administration`}
          onAction={returnFromImpersonation}
        />
      </Box>
    );
  }

  if (!impersonation) return null;

  const bannerState =
    impersonationStatus === "terminal"
      ? "terminal"
      : impersonationStatus === "ending"
        ? "ending"
        : "active";
  return (
    <ImpersonationBanner
      binding={toBannerBinding(impersonation)}
      state={bannerState}
      terminalCode={impersonationTerminalCode}
      onExit={() => void exitImpersonation()}
      onReturn={returnFromImpersonation}
    />
  );
}

export default TenantAuthWrapper;
